
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Project } from '@/types/crm';

export interface ProjectTemplate {
  id: string;
  name: string;
  description: string | null;
  schema: any;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export const useProjects = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [templates, setTemplates] = useState<ProjectTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchProjects = async () => {
    try {
      // First fetch projects with organization
      const { data: projectsData, error: projectsError } = await supabase
        .from('projects')
        .select(`
          *,
          organization:organizations(id, name, sector)
        `)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (projectsError) throw projectsError;

      // Get unique owner IDs to fetch profiles
      const ownerIds = [...new Set(projectsData?.map(p => p.owner_id).filter(Boolean))] as string[];
      
      // Fetch owner profiles if there are any
      let profilesMap: Record<string, any> = {};
      if (ownerIds.length > 0) {
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('id, display_name, email, avatar_url, avatar_bg_color')
          .in('id', ownerIds);
        
        if (profilesData) {
          profilesMap = Object.fromEntries(profilesData.map(p => [p.id, p]));
        }
      }

      // Transform the data to ensure proper types
      const transformedData = projectsData?.map(project => {
        const organization = Array.isArray(project.organization) 
          ? project.organization[0] 
          : project.organization;
        
        return {
          ...project,
          organization,
          owner: project.owner_id ? profilesMap[project.owner_id] || null : null,
          sharepoint_links: (project.sharepoint_links as any) || [],
          project_images: (project.project_images as any) || []
        };
      }) || [];
      
      setProjects(transformedData as any);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast({
        title: 'Fout bij laden projecten',
        description: 'Er ging iets mis bij het ophalen van de projecten.',
        variant: 'destructive'
      });
    }
  };

  const fetchTemplates = async (retryCount = 0) => {
    try {
      const { data, error } = await supabase
        .from('project_templates')
        .select('*')
        .is('deleted_at', null)
        .order('name');

      if (error) throw error;
      setTemplates(data || []);
    } catch (error: any) {
      console.error('Error fetching templates:', {
        message: error?.message || 'Unknown error',
        code: error?.code || '',
        details: error
      });
      
      // Silent retry for network errors (max 2 retries)
      if (error?.message === 'Failed to fetch' && retryCount < 2) {
        setTimeout(() => fetchTemplates(retryCount + 1), 2000);
        return;
      }
      
      // Only show toast for non-network errors or after retries exhausted
      if (error?.message !== 'Failed to fetch') {
        toast({
          title: 'Fout bij laden templates',
          description: error?.message || 'Er ging iets mis bij het ophalen van de templates.',
          variant: 'destructive'
        });
      }
    }
  };

  const createProject = async (projectData: any) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const projectWithOwner = {
        ...projectData,
        owner_id: user.id
      };

      const { data, error } = await supabase
        .from('projects')
        .insert(projectWithOwner)
        .select()
        .single();

      if (error) throw error;

      const transformedData = {
        ...data,
        sharepoint_links: (data.sharepoint_links as any) || [],
        project_images: (data.project_images as any) || []
      };
      setProjects(prev => [transformedData, ...prev]);
      toast({
        title: 'Project aangemaakt',
        description: 'Het project is succesvol aangemaakt.',
      });

      return data;
    } catch (error) {
      console.error('Error creating project:', error);
      toast({
        title: 'Fout bij aanmaken project',
        description: 'Er ging iets mis bij het aanmaken van het project.',
        variant: 'destructive'
      });
      throw error;
    }
  };

  const updateProject = async (id: string, updates: any) => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      const transformedData = {
        ...data,
        sharepoint_links: (data.sharepoint_links as any) || [],
        project_images: (data.project_images as any) || []
      };
      setProjects(prev => prev.map(project => 
        project.id === id ? { ...project, ...transformedData } : project
      ));

      toast({
        title: 'Project bijgewerkt',
        description: 'Het project is succesvol bijgewerkt.',
      });
    } catch (error) {
      console.error('Error updating project:', error);
      toast({
        title: 'Fout bij bijwerken project',
        description: 'Er ging iets mis bij het bijwerken van het project.',
        variant: 'destructive'
      });
    }
  };

  /**
   * Archiveert een project (soft delete). Elke lees-query filtert al op
   * `deleted_at is null`, dus het verdwijnt uit beeld maar blijft herstelbaar
   * via /crm/archief. Definitief verwijderen kan alleen een super_admin, via
   * `permanentlyDeleteProject`.
   */
  const archiveProject = async (id: string) => {
    try {
      const { error } = await supabase
        .from('projects')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      setProjects(prev => prev.filter(project => project.id !== id));
      toast({
        title: 'Project gearchiveerd',
        description: 'Je vindt het terug in het archief en kunt het daar herstellen.',
      });
    } catch (error) {
      console.error('Error archiving project:', error);
      toast({
        title: 'Archiveren mislukt',
        description: 'Er ging iets mis bij het archiveren van het project.',
        variant: 'destructive'
      });
      throw error;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchProjects(), fetchTemplates()]);
      setLoading(false);
    };
    fetchData();
  }, []);

  /**
   * Verwijdert een project definitief uit de database. Onherstelbaar en
   * cascadeert naar alles wat eraan hangt. Alleen aanroepen achter een
   * `hasRole('super_admin')`-check; de normale weg is `archiveProject`.
   */
  const permanentlyDeleteProject = async (id: string) => {
    const { error } = await supabase.from('projects').delete().eq('id', id);
    if (error) throw error;
    setProjects(prev => prev.filter(project => project.id !== id));
  };

  return {
    projects,
    templates,
    loading,
    refetch: () => Promise.all([fetchProjects(), fetchTemplates()]),
    createProject,
    updateProject,
    archiveProject,
    permanentlyDeleteProject
  };
};
